import { memo, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';
import Collapse from '../Collapse';

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

function fmtAmt(n) {
  const x = Number(n) || 0;
  if (x >= 100000) return `${x / 100000}L`;
  if (x >= 1000) return `${x / 1000}K`;
  return String(x);
}

function bookOf(exposures, name, sid) {
  return roundBook(exposures?.[`sid:${sid}`] ?? exposures?.[name] ?? 0);
}

function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

const PLAYERS = ['Player 8', 'Player 9', 'Player 10', 'Player 11'];
const COLORS = [
  { label: 'Any 3 Card Black', nat: 'Any Three Card Black', rangeId: 'demo-fancy1-0' },
  { label: 'Any 3 Card Red', nat: 'Any Three Card Red', rangeId: 'demo-fancy1-1' },
  { label: 'Two Black Two Red', nat: 'Two Black Two Red', rangeId: 'demo-fancy1-2' },
];
const TOTALS = [
  { label: '8 & 9 Total', nat: '8 & 9 Total', rangeId: 'demo-fancy-18' },
  { label: '10 & 11 Total', nat: '10 & 11 Total', rangeId: 'demo-fancy-19' },
];
const SINGLE_LABELS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

// range collapse (i + icon-range), rendered inside caller's float-right
function Range({ rangeId, min = 100, max = 100000 }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <i
        data-toggle="collapse"
        data-target={`#${rangeId}`}
        className="fas fa-info-circle"
        onClick={() => setOpen((o) => !o)}
      ></i>
      {' '}
      <Collapse id={rangeId} open={open} className="icon-range">
        R:
        <span>{fmtAmt(min)}</span>
        -
        <span>{fmtAmt(max)}</span>
      </Collapse>
    </>
  );
}

const BetTableCard32EU = memo(function BetTableCard32EU({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});
  const find = (nat) => sub.find((s) => s.nat === nat);

  // header title row
  const titleRow = (cols, mb0) => (
    <div className={`casino-box-row${mb0 ? ' mb-0' : ''}`}>
      <div className="casino-nation-name no-border casino-bl-box-title"><div className="playerb"></div></div>
      <div className="casino-bl-box casino-bl-box-title">
        {cols.map((c, i) => <div key={i} className="casino-bl-box-item"><b>{c}</b></div>)}
      </div>
    </div>
  );

  // nation-name with book (winner / color / total)
  const nameBook = (label, item, rangeId, mobile) => {
    const book = bookOf(exposures, item?.nat ?? label, item?.sid);
    const max = item?.max ?? 100000;
    const min = item?.min ?? 100;
    if (mobile) {
      return (
        <div className="casino-nation-name">
          <div><b>{label}</b> <span className={`d-block w-100 ${bookCls(book)}`}>{fmtBook(book)}</span></div>
          {' '}
          <div className="float-right"><Range rangeId={rangeId} min={min} max={max} /></div>
        </div>
      );
    }
    return (
      <div className="casino-nation-name">
        <b>{label}</b>
        {' '}
        <div className="float-right">
          <span className={`mr-2 casino-book ${bookCls(book)}`}>{fmtBook(book)}</span>
          {' '}
          <Range rangeId={rangeId} min={min} max={max} />
        </div>
      </div>
    );
  };

  // nation-name without book (odd/even)
  const namePlain = (label, item, rangeId) => (
    <div className="casino-nation-name">
      <b>{label}</b>
      {' '}
      <div className="float-right"><Range rangeId={rangeId} min={item?.min ?? 100} max={item?.max ?? 100000} /></div>
    </div>
  );

  const backItem = (item, val) => (
    <div
      className={`back casino-bl-box-item${suspCls(item)}`}
      onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
    >
      <span className="casino-box-odd">{val ?? item?.b ?? 0}</span>
    </div>
  );

  const layItem = (item) => (
    <div
      className={`lay casino-bl-box-item${suspCls(item)}`}
      onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'lay', item.l, item)}
    >
      <span className="casino-box-odd">{item?.l ?? 0}</span>
    </div>
  );

  // back/lay row (winner + color)
  const backLayRow = (label, item, rangeId, mobile) => (
    <div className="casino-box-row" key={rangeId}>
      {nameBook(label, item, rangeId, mobile)}
      <div className="casino-bl-box">
        {backItem(item)}
        {layItem(item)}
      </div>
    </div>
  );

  // odd/even row — two back boxes, each with book
  const oddEvenRow = (name, idx, mobile) => {
    const num = name.replace('Player ', '');
    const oddItem = find(`Player ${num} Odd`);
    const evenItem = find(`Player ${num} Even`);
    const oddBook = bookOf(exposures, oddItem?.nat, oddItem?.sid);
    const evenBook = bookOf(exposures, evenItem?.nat, evenItem?.sid);
    const bookPre = mobile ? '' : 'casino-book ';
    const cell = (item, book) => (
      <div
        className={`back casino-bl-box-item${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
      >
        <span className="casino-box-odd">{item?.b ?? 0}</span>
        {' '}
        <span className={`${bookPre}${bookCls(book)}`}>{fmtBook(book)}</span>
      </div>
    );
    return (
      <div className="casino-box-row" key={`oe-${idx}`}>
        {namePlain(name, oddItem, `demo-odd-evan-${idx + 1}`)}
        <div className="casino-bl-box">
          {cell(oddItem, oddBook)}
          {cell(evenItem, evenBook)}
        </div>
      </div>
    );
  };

  // total row — desktop single back, mobile two backs
  const totalRow = (t, mobile) => {
    const item = find(t.nat);
    return (
      <div className="casino-box-row" key={t.rangeId}>
        {nameBook(t.label, item, t.rangeId, mobile)}
        <div className="casino-bl-box">
          {backItem(item)}
          {mobile && backItem(item)}
        </div>
      </div>
    );
  };

  // single-digit odds strip
  const headerOdd = find('Single 1')?.b ?? 0;
  const singleStrip = (mobile) => (
    <div className="casino-bl-box">
      {SINGLE_LABELS.map((label) => {
        const item = find(`Single ${label}`);
        const book = bookOf(exposures, item?.nat, item?.sid);
        return (
          <div
            key={label}
            className={`casino-bl-box-item back${suspCls(item)}`}
            onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
          >
            <span className="casino-box-odd">{label}</span>
            {' '}
            {mobile
              ? <span className={bookCls(book)}>{fmtBook(book)}</span>
              : <div className={`casino-book ${bookCls(book)}`}>{fmtBook(book)}</div>}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {/* ── DESKTOP ── */}
      <div className="teen1daycasino-container d-none-small">
        <div className="teen1dayleft">
          {titleRow(['Back', 'Lay'], true)}
          {PLAYERS.map((name, i) => backLayRow(name, find(name), `demo-winner-${i}`, false))}
        </div>
        <div className="teen1daycenter"></div>
        <div className="teen1dayright">
          {titleRow(['Odd', 'Even'], true)}
          {PLAYERS.map((name, i) => oddEvenRow(name, i, false))}
        </div>
      </div>

      <div className="teen1daycasino-container mt-4 d-none-small">
        <div className="teen1dayleft">
          {titleRow(['Back', 'Lay'], true)}
          {COLORS.map((c) => backLayRow(c.label, find(c.nat), c.rangeId, false))}
        </div>
        <div className="teen1daycenter"></div>
        <div className="teen1dayright right1">
          {titleRow(['Back'], true)}
          {TOTALS.map((t) => totalRow(t, false))}
        </div>
      </div>

      <div className="text-center mt-4 d-none-small pr casino-cards-odds-title">
        <b>{headerOdd}</b>
        {' '}
        <div className="d-inline-block ml-1 float-right"><Range rangeId="demo-single" max={25000} /></div>
      </div>
      <div className="d-none-small cards32bextra mt-1">
        {singleStrip(false)}
      </div>

      {/* ── MOBILE ── */}
      <div className="teen1daycasino-container d-none-big">
        {titleRow(['Back', 'Lay'], false)}
        {PLAYERS.map((name, i) => backLayRow(name, find(name), `demo-winner-${i}`, true))}
        {titleRow(['Odd', 'Even'], false)}
        {PLAYERS.map((name, i) => oddEvenRow(name, i, true))}
        {titleRow(['Back', 'Lay'], false)}
        {COLORS.map((c) => backLayRow(c.label, find(c.nat), c.rangeId, true))}
        {titleRow(['Back', 'Back'], false)}
        {TOTALS.map((t) => totalRow(t, true))}
        <div className="text-center w-100 pr casino-cards-odds-title">
          <b>{headerOdd}</b>
          {' '}
          <div className="d-inline-block ml-1 float-right"><Range rangeId="demo-single" max={25000} /></div>
        </div>
        <div className="cards32bextra">
          {singleStrip(true)}
        </div>
      </div>
    </>
  );
});

export default BetTableCard32EU;
